/**
 * Activating one draft mailing or call round by hand: "Mark ready now" and
 * "Open now" on the campaign plan (decided, product owner, 16 Oct 2026;
 * docs/features/direct-mail-campaigns/ spec 03 §5.10). The daily
 * auto-activate job may or may not be running, so nothing here promises it
 * will unless the caller says it is on.
 */
import type { CadenceChannel, TouchSkipReason } from "../data/api-types";
import { activationDateFor } from "./campaign-calendar";
import { notIncludedReasonLabel } from "./direct-mail-campaigns";
import { formatDateOrDash } from "./formatters";

/** The channels a person activates from the plan (an email send has its own
 *  Activate in its send detail). */
export type ActivatableNowChannel = "direct_mail" | "call";

export function isActivatableNow(
  channel: CadenceChannel,
): channel is ActivatableNowChannel {
  return channel === "direct_mail" || channel === "call";
}

export const ACTIVATE_NOW_LABELS: Record<ActivatableNowChannel, string> = {
  direct_mail: "Mark ready now",
  call: "Open now",
};

/** The confirm button in the dialog. */
export const ACTIVATE_NOW_CONFIRM_LABELS: Record<
  ActivatableNowChannel,
  string
> = {
  direct_mail: "Mark ready",
  call: "Open call round",
};

/** What a draft mailing or call round tells the marketer about when its
 *  recipients are fixed. Promises the job only when `autoActivationEnabled`
 *  is true (and the step is dated); paused or unknown, it promises nothing. */
export function draftStepHint(
  channel: ActivatableNowChannel,
  date: string | null,
  autoActivationEnabled: boolean | null,
): string {
  const lead =
    channel === "direct_mail"
      ? "Mark it ready when you're about to pack it; this fixes who it goes to."
      : "Open it when you're ready to start calling; this fixes who it goes to and raises a call ticket for each of them.";
  if (autoActivationEnabled !== true || !date) return lead;
  const on = formatDateOrDash(activationDateFor(date));
  return channel === "direct_mail"
    ? `${lead} Otherwise One will mark it ready on ${on}, the working day before.`
    : `${lead} Otherwise One will open it on ${on}, the working day before.`;
}

function people(n: number): string {
  return n === 1 ? "1 person" : `${n} people`;
}

/** The confirm dialog's sentence: who it will go to, who it leaves out and
 *  why, and that the list is then fixed — "This mailing will go to 9 people
 *  (1 not included: no address). Their list is then fixed."
 *
 *  `notIncludedReasons` are the skip reasons of the people left out that
 *  the caller has in hand; the breakdown is given only when that is all of
 *  them (`notIncludedTotal`), else just the count. */
export function activateNowConfirmation(
  channel: ActivatableNowChannel,
  includedCount: number,
  notIncludedTotal: number,
  notIncludedReasons: TouchSkipReason[],
): string {
  const reach =
    channel === "direct_mail"
      ? includedCount === 0
        ? "This mailing will go to nobody"
        : `This mailing will go to ${people(includedCount)}`
      : includedCount === 0
        ? "This call round will raise no call tickets"
        : `This call round will raise a call ticket for each of ${people(includedCount)}`;
  const fixed = "Their list is then fixed.";
  if (notIncludedTotal === 0) return `${reach}. ${fixed}`;
  if (notIncludedReasons.length !== notIncludedTotal) {
    return `${reach} (${notIncludedTotal} not included). ${fixed}`;
  }
  const counts = new Map<string, number>();
  for (const reason of notIncludedReasons) {
    const label = notIncludedReasonLabel(channel, reason);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  // One reason covers everyone left out: "(2 not included: no address)".
  const breakdown =
    counts.size === 1
      ? [...counts.keys()][0]
      : [...counts.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([label, count]) => `${count} ${label}`)
          .join(", ");
  return `${reach} (${notIncludedTotal} not included: ${breakdown}). ${fixed}`;
}
